import * as Yup from "yup";
import { JOURNEY_EMAIL_REGEX } from "../../../../utils/emailValidation";

export function buildCoApplicantValidationSchema(coApplicantFields: any[], requirePayParty: boolean = false) {
  if (!coApplicantFields || coApplicantFields.length === 0) {
    return Yup.object().shape({});
  }

  const schemaShape: any = {};

  coApplicantFields.forEach((field: any) => {
    let fieldSchema: any;
    const otherRelationMatch = String(field?.name || "").match(
      /^basicCoApplicant(\d+)(OtherRelationWithStudent|OtherRelationship|RelationValue)$/,
    );
    const isConditional = !!field?.condition?.field;
    const isRequired = !!field?.required;
    const applyConditionalRequirement = (schema: any) => {
      if (otherRelationMatch) {
        const relationFieldName = `basicCoApplicant${otherRelationMatch[1]}RelationWithStudent`;

        return schema.when(relationFieldName, {
          is: (value: any) => String(value ?? "").trim().toUpperCase() === "OTHERS",
          then: (nextSchema: any) =>
            nextSchema.required(`${field.label} is required`),
          otherwise: (nextSchema: any) => nextSchema.optional(),
        });
      }

      if (!isConditional) {
        return isRequired ? schema.required(`${field.label} is required`) : schema;
      }

      return schema.when(field.condition.field, {
        is: (value: any) =>
          String(value ?? "").trim().toLowerCase() ===
          String(field.condition.value ?? "").trim().toLowerCase(),
        then: (nextSchema: any) =>
          isRequired ? nextSchema.required(`${field.label} is required`) : nextSchema,
        otherwise: (nextSchema: any) => nextSchema.optional(),
      });
    };

    switch (field.type) {
      case "text":
        if (field.name.includes("FirstName") || field.name.includes("LastName")) {
          fieldSchema = Yup.string()
            .matches(/^[A-Za-z\s]*$/, "Name should only contain letters")
            .test(
              "min-length-if-filled",
              "Name must be at least 2 characters",
              (value) => {
                if (!value || value.trim() === "") return !field.required;
                return value.trim().length >= 2;
              }
            )
            .max(26, "Name must not exceed 26 characters");
          fieldSchema = applyConditionalRequirement(fieldSchema);
        } else if (field.name.includes("PanNumber")) {
          fieldSchema = Yup.string().test(
            "pan-format",
            "PAN must be in format AAAAA9999A",
            (value) => {
              if (!value) return true;
              if (value.length !== 10) return true;
              return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value) ? true : false;
            }
          );
          fieldSchema = applyConditionalRequirement(fieldSchema);
        } else if (field.name.includes("OtherRelationWithStudent")) {
          fieldSchema = Yup.string()
            .matches(/^[A-Za-z\s]*$/, "Only alphabetic characters and spaces are allowed.")
            .max(50, "Maximum 50 characters allowed.");
          fieldSchema = applyConditionalRequirement(fieldSchema);
        } else {
          fieldSchema = Yup.string()
            .matches(/^[A-Za-z\s]*$/, "This field should only contain letters")
            .max(26, "This field must not exceed 26 characters");
          fieldSchema = applyConditionalRequirement(fieldSchema);
        }
        break;
      case "email":
        fieldSchema = Yup.string()
          .email("Invalid email id")
          .matches(JOURNEY_EMAIL_REGEX, { message: "Invalid email id", excludeEmptyString: true })
          .max(40, "Invalid email id");
        fieldSchema = applyConditionalRequirement(fieldSchema);
        break;
      case "phone":
      case "tel":
        fieldSchema = Yup.string()
          .matches(/^[0-9]{0,10}$/, "Phone number must contain only digits")
          .test("exact-length", "Mobile number must be 10 digits", (value) => {
            if (!value || value === "") return !field.required;
            return value.length === 10;
          });
        fieldSchema = applyConditionalRequirement(fieldSchema);
        break;
      case "date":
        fieldSchema = Yup.date()
          .typeError("Please enter a valid date")
          .max(new Date(), "Date cannot be in the future");
        fieldSchema = applyConditionalRequirement(fieldSchema);
        break;
      case "dropdown":
      case "select":
        fieldSchema = Yup.string().notOneOf(
          [""],
          `Please select a ${String(field.label).toLowerCase()}`
        );
        fieldSchema = applyConditionalRequirement(fieldSchema);
        break;
      case "radio":
        if (field.name.includes("PayParty")) {
            fieldSchema = requirePayParty
            ? Yup.string().trim().required("Pay Party is required")
            : Yup.string().trim().optional()
        }
        if(field.name.includes("isEmployed")) {
          Yup.string().trim().required("Currently Employed is required")
        }
        break;
      default:
        fieldSchema = applyConditionalRequirement(Yup.string());
    }

    schemaShape[field.name] = fieldSchema;
  });

  return Yup.object().shape(schemaShape);
}

